import React, { useEffect, useState } from "react";
import { Mail, Server, ShieldCheck } from "lucide-react";
import { smtpSettingsApi, SmtpSettings as SmtpSettingsT } from "../../api/smtpSettings";
import { useToast } from "../../hooks/useToast";

/** Refined pill switch built on the Teamlio (--gt-*) design tokens. */
function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      data-on={checked ? "true" : "false"}
      onClick={() => onChange(!checked)}
      className="smset-switch"
    >
      <span className="smset-knob" />
    </button>
  );
}

export default function SmtpSettings() {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SmtpSettingsT | null>(null);

  const [host, setHost] = useState("smtp.gmail.com");
  const [port, setPort] = useState<number>(587);
  const [portInput, setPortInput] = useState("587");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [senderName, setSenderName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [useTls, setUseTls] = useState(true);

  // From Email only makes sense to expose for Gmail — Gmail requires it to be
  // a verified "Send mail as" alias of the authenticated account, and this
  // form is Gmail-only for now, so a mismatched From/host is never valid.
  const isGmailHost = host.trim().toLowerCase() === "smtp.gmail.com";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await smtpSettingsApi.get();
        if (cancelled) return;
        const s = r.data?.settings || null;
        const defaults = r.data?.defaults;
        setSettings(s);
        if (s) {
          setHost(s.host || defaults?.host || "smtp.gmail.com");
          setPort(s.port || defaults?.port || 587);
          setPortInput(String(s.port || defaults?.port || 587));
          setUsername(s.username || "");
          setSenderName(s.sender_name || "");
          setFromEmail(s.from_email || "");
          setUseTls(s.use_tls ?? true);
        } else if (defaults) {
          // First-time setup — pre-fill Gmail's SMTP defaults.
          setHost(defaults.host);
          setPort(defaults.port);
          setPortInput(String(defaults.port));
        }
      } catch (e: any) {
        toast({
          variant: "destructive",
          description: e?.response?.data?.detail || "Failed to load SMTP settings",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!host.trim() || !username.trim()) {
      toast({ variant: "destructive", description: "SMTP host and email are required." });
      return;
    }
    if (!settings?.has_password && !password.trim()) {
      toast({ variant: "destructive", description: "App password is required for first-time setup." });
      return;
    }

    setSaving(true);
    try {
      const payload: Parameters<typeof smtpSettingsApi.save>[0] = {
        host: host.trim(),
        port,
        username: username.trim(),
        sender_name: senderName.trim(),
        // From Email is only exposed for non-Gmail hosts — for Gmail, don't
        // touch whatever value is already stored.
        from_email: isGmailHost ? undefined : fromEmail.trim(),
        use_tls: useTls,
      };
      if (password.trim()) payload.password = password.trim();

      const r = await smtpSettingsApi.save(payload);
      const s: SmtpSettingsT = r.data?.settings;
      if (s) setSettings(s);
      setPassword("");
      toast({ description: "SMTP settings saved." });
    } catch (e: any) {
      toast({
        variant: "destructive",
        description: e?.response?.data?.detail || "Failed to save SMTP settings",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="smset-scope">
        <Style />
        <div className="smset-wrap">
          <div className="smset-meta" style={{ padding: 28 }}>Loading email settings…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="smset-scope">
      <Style />
      <div className="smset-wrap">
        <header className="smset-rise" style={{ animationDelay: "0ms" }}>
          <div className="smset-eyebrow">Workspace · Email</div>
          <h1 className="smset-display">Email (SMTP) Settings</h1>
          <p className="smset-sub">
            Send emails through your own mail server instead of the shared account.
            Use a Google App Password, not your regular Gmail password.
          </p>
        </header>

        <section className="smset-card smset-rise" style={{ animationDelay: "90ms" }}>
          <div className="smset-chead">
            <span className="smset-badge"><Mail size={16} /></span>
            <div>
              <div className="smset-eyebrow">Sender</div>
              <h2 className="smset-ctitle">Account</h2>
              <p className="smset-cdesc">The mailbox emails will be sent from.</p>
            </div>
          </div>

          <div className="smset-fields">
            <div>
              <label className="smset-flabel">Email</label>
              <input
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="you@company.com"
                className="smset-input"
              />
            </div>

            <div>
              <label className="smset-flabel">App Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={settings?.has_password ? "Leave blank to keep current password" : "16-character app password"}
                className="smset-input"
              />
              <p className="smset-hint">
                Not your Gmail login password — generate one under Google Account → Security → App Passwords.
              </p>
            </div>

            <div>
              <label className="smset-flabel">Sender Name</label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="(optional) e.g. Teamlio HR"
                className="smset-input"
              />
            </div>

            {!isGmailHost && (
              <div>
                <label className="smset-flabel">From Email</label>
                <input
                  type="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  placeholder="(optional) defaults to the email above"
                  className="smset-input"
                />
              </div>
            )}
          </div>
        </section>

        <section className="smset-card smset-rise" style={{ animationDelay: "170ms" }}>
          <div className="smset-chead">
            <span className="smset-badge"><Server size={16} /></span>
            <div>
              <div className="smset-eyebrow">Server</div>
              <h2 className="smset-ctitle">SMTP Server</h2>
              <p className="smset-cdesc">Defaults are Google's SMTP — change only for a different provider.</p>
            </div>
          </div>

          <div className="smset-fields">
            <div>
              <label className="smset-flabel">SMTP Host</label>
              <input
                type="text"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="smtp.gmail.com"
                className="smset-input"
              />
            </div>

            <div>
              <label className="smset-flabel">Port</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={portInput}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setPortInput(digits);
                  setPort(digits === "" ? 0 : parseInt(digits, 10));
                }}
                placeholder="587"
                className="smset-input"
              />
              <p className="smset-hint">587 = STARTTLS (Gmail default), 465 = implicit TLS.</p>
            </div>
          </div>

          <div className="smset-master">
            <span className="smset-ico smset-ico-on"><ShieldCheck size={18} /></span>
            <div className="smset-rgrow">
              <p className="smset-rtitle">Use TLS</p>
              <p className="smset-rdesc">Keep this on unless your mail server explicitly requires plain SMTP.</p>
            </div>
            <Toggle checked={useTls} onChange={setUseTls} label="Use TLS" />
          </div>

          {settings && (
            <p className="smset-cardmeta">
              {settings.has_password ? "Password configured" : "No password saved yet"}
              {settings.updated_at && ` · updated ${new Date(settings.updated_at).toLocaleDateString()}`}
            </p>
          )}
        </section>

        <div className="smset-actionbar smset-rise" style={{ animationDelay: "240ms" }}>
          <p className="smset-meta">These settings apply to everyone in this branch.</p>
          <button onClick={handleSave} disabled={saving} className="smset-btn smset-btn-lg">
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Scoped styles — consume the global Teamlio (--gt-*) tokens directly so the
 *  page is on-brand regardless of the .gt-themed utility remap. */
function Style() {
  return (
    <style>{`
      .smset-wrap { max-width: 74rem; margin: 0 auto; padding: 0 2px 14px;
        display: flex; flex-direction: column; gap: 12px; }

      @keyframes smset-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
      .smset-rise { animation: smset-rise var(--gt-t-slow) both; }

      .smset-eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: var(--gt-letter-eyebrow);
        text-transform: uppercase; color: var(--gt-text-tertiary); }
      .smset-display { margin: 3px 0 4px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 500; font-size: clamp(24px, 2.6vw, 30px); line-height: 1.03;
        letter-spacing: -0.01em; color: var(--gt-primary); }
      .smset-sub { margin: 0; color: var(--gt-text-secondary); font-size: 13px; line-height: 1.45; max-width: 70ch; }

      .smset-card { position: relative; background: var(--gt-surface-0); border: 1px solid var(--gt-border);
        border-radius: var(--gt-radius-card); box-shadow: var(--gt-elev-2); padding: 16px 18px; overflow: hidden; }
      .smset-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent); }

      .smset-chead { display: flex; align-items: flex-start; gap: 11px; margin-bottom: 12px; }
      .smset-badge { flex: none; width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
        background: var(--gt-primary-container); color: var(--gt-primary); margin-top: 1px; }
      .smset-ctitle { margin: 1px 0 3px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 600; font-size: 18px; line-height: 1.1; color: var(--gt-text-primary); }
      .smset-cdesc { margin: 0; font-size: 12px; line-height: 1.45; color: var(--gt-text-secondary); max-width: 58ch; }

      .smset-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
      @media (max-width: 640px) { .smset-fields { grid-template-columns: 1fr; } }
      .smset-flabel { display: block; margin-bottom: 4px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 500; font-size: 13px; color: var(--gt-text-secondary); }
      .smset-input { width: 100%; box-sizing: border-box; font-family: var(--gt-font-mono) !important; font-size: 13.5px;
        padding: 8px 12px; border-radius: var(--gt-radius-input); background: var(--gt-surface-1);
        border: 1px solid var(--gt-border); color: var(--gt-text-primary);
        transition: border-color var(--gt-t-fast), box-shadow var(--gt-t-fast), background var(--gt-t-fast); }
      .smset-input::placeholder { color: var(--gt-text-disabled); }
      .smset-input:focus { outline: none; border-color: var(--gt-primary); background: var(--gt-surface-0);
        box-shadow: 0 0 0 3px rgba(15, 45, 31, 0.10); }
      .smset-hint { margin: 4px 0 0; font-size: 11px; line-height: 1.35; color: var(--gt-text-tertiary); }

      .smset-cardmeta { margin: 12px 0 0; padding-top: 10px; border-top: 1px solid var(--gt-divider);
        font-size: 11.5px; color: var(--gt-text-tertiary); }

      .smset-actionbar { position: sticky; bottom: 8px; z-index: 5;
        display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
        margin-top: 2px; padding: 11px 12px 11px 18px; background: var(--gt-surface-0);
        border: 1px solid var(--gt-border); border-radius: 14px; box-shadow: var(--gt-elev-4); }
      .smset-meta { margin: 0; font-size: 12px; color: var(--gt-text-tertiary); }
      .smset-btn-lg { padding: 11px 24px; font-size: 14px; }

      .smset-master { display: flex; align-items: center; gap: 12px; padding: 11px 13px; margin-top: 12px;
        border-radius: 13px; background: var(--gt-primary-container); border: 1px solid var(--gt-accent-soft); }
      .smset-rgrow { flex: 1 1 auto; min-width: 0; }
      .smset-ico { flex: none; width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center;
        background: var(--gt-surface-2); color: var(--gt-primary-soft); }
      .smset-ico-on { background: var(--gt-surface-0); color: var(--gt-primary); border: 1px solid var(--gt-accent-soft); }
      .smset-rtitle { margin: 0; font-weight: 600; font-size: 14px; color: var(--gt-text-primary); }
      .smset-rdesc { margin: 2px 0 0; font-size: 11.5px; line-height: 1.4; color: var(--gt-text-tertiary); }

      .smset-switch { position: relative; flex: none; width: 44px; height: 25px; border-radius: 9999px;
        cursor: pointer; padding: 0; border: 1px solid var(--gt-border-strong); background: var(--gt-surface-3);
        transition: background var(--gt-t-mid), border-color var(--gt-t-mid); }
      .smset-switch[data-on="true"] { background: var(--gt-primary); border-color: var(--gt-primary); }
      .smset-switch:disabled { opacity: 0.45; cursor: not-allowed; }
      .smset-switch:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(15, 45, 31, 0.18); }
      .smset-knob { position: absolute; top: 50%; left: 2px; width: 18px; height: 18px; border-radius: 9999px;
        background: #ffffff; box-shadow: var(--gt-elev-2); transform: translate(0, -50%);
        transition: transform var(--gt-t-mid); }
      .smset-switch[data-on="true"] .smset-knob { transform: translate(19px, -50%); }

      .smset-btn { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; font-size: 14px;
        padding: 10px 20px; border: none; border-radius: 12px; color: #ffffff !important; background: var(--gt-primary);
        cursor: pointer; box-shadow: var(--gt-glow-soft);
        transition: transform var(--gt-t-fast), background var(--gt-t-fast), box-shadow var(--gt-t-fast); }
      .smset-btn:hover:not(:disabled) { background: var(--gt-primary-hover); transform: translateY(-1px); box-shadow: var(--gt-glow); }
      .smset-btn:active:not(:disabled) { transform: translateY(0); }
      .smset-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    `}</style>
  );
}
